import { useState, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePersons } from '../../hooks/useApi';
import { resolveImageUrl, type Person } from '../../api';

interface PersonExplorerProps {
  onSelectPerson: (slug: string) => void;
  selectedSlug: string | null;
  filters: Record<string, any>;
}

// Muted, sophisticated category colors that work on dark backgrounds
// Each has a bg (for badges/avatars) and text (for readable text on that bg)
const CATEGORY_PALETTE: Record<string, { bg: string; text: string; border: string }> = {
  politician:              { bg: 'rgba(220, 100, 100, 0.18)', text: '#e8a0a0', border: '#c46a6a' },
  business:                { bg: 'rgba(100, 190, 130, 0.18)', text: '#90d4a8', border: '#5eaa78' },
  royalty:                 { bg: 'rgba(220, 190, 100, 0.18)', text: '#dcc878', border: '#b8a44e' },
  celebrity:               { bg: 'rgba(120, 140, 240, 0.18)', text: '#a0b0f0', border: '#6878d0' },
  associate:               { bg: 'rgba(180, 130, 190, 0.18)', text: '#c8a0d4', border: '#9a6aaa' },
  legal:                   { bg: 'rgba(160, 120, 200, 0.18)', text: '#b898d4', border: '#8a60b8' },
  academic:                { bg: 'rgba(80, 200, 180, 0.18)',  text: '#80d4c4', border: '#40a898' },
  socialite:               { bg: 'rgba(220, 160, 100, 0.18)', text: '#d8b880', border: '#b89060' },
  'military-intelligence': { bg: 'rgba(100, 140, 180, 0.18)', text: '#90b8d4', border: '#5888b0' },
  other:                   { bg: 'rgba(150, 150, 170, 0.18)', text: '#a0a0b8', border: '#707088' },
};

const CATEGORIES = Object.keys(CATEGORY_PALETTE);

function getCategoryStyle(category: string) {
  return CATEGORY_PALETTE[category] || CATEGORY_PALETTE.other;
}

function formatLabel(cat: string): string {
  if (cat === 'military-intelligence') return 'Mil-Intel';
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}

function truncateBio(bio: string | null | undefined, max = 150): string {
  if (!bio) return '';
  if (bio.length <= max) return bio;
  // Break at last space before max
  const truncated = bio.slice(0, max);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? truncated.slice(0, lastSpace) : truncated) + '\u2026';
}

function StatBadge({ value, label }: { value: number; label: string }) {
  if (value === 0) return null;
  return (
    <span className="person-stat-badge">
      <span className="person-stat-value">{value.toLocaleString()}</span>
      <span className="person-stat-label">{label}</span>
    </span>
  );
}

// ─── PersonCard ──────────────────────────────────────────────

function PersonCard({
  person,
  selected,
  onClick,
}: {
  person: Person;
  selected: boolean;
  onClick: () => void;
}) {
  const palette = getCategoryStyle(person.category);
  const [imgError, setImgError] = useState(false);
  const resolvedImg = resolveImageUrl(person.image_url);
  const showImage = resolvedImg && !imgError;

  return (
    <button
      className={`person-card${selected ? ' selected' : ''}`}
      onClick={onClick}
      style={{ '--card-accent': palette.border } as React.CSSProperties}
    >
      {/* Gradient top border */}
      <div
        className="person-card-accent-bar"
        style={{
          background: `linear-gradient(90deg, ${palette.border}, transparent)`,
        }}
      />

      <div className="person-card-body">
        {/* Avatar / Photo */}
        <div className="person-card-avatar-wrap">
          {showImage ? (
            <img
              className="person-card-avatar-img"
              src={resolvedImg!}
              alt={person.name}
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div
              className="person-card-avatar-fallback"
              style={{ background: palette.bg, color: palette.text }}
            >
              {getInitials(person.name)}
            </div>
          )}
        </div>

        {/* Info block */}
        <div className="person-card-content">
          <div className="person-card-title-row">
            <span className="person-card-name">{person.name}</span>
            <div className="person-card-badges">
              <span
                className="person-card-category"
                style={{ background: palette.bg, color: palette.text, borderColor: palette.border }}
              >
                {formatLabel(person.category)}
              </span>
              {person.black_book_entry && (
                <span className="person-card-bb" title="In Epstein's Black Book">BB</span>
              )}
            </div>
          </div>

          {person.short_bio && (
            <p className="person-card-bio">{truncateBio(person.short_bio)}</p>
          )}

          <div className="person-card-stats">
            <StatBadge value={person.stats.documents} label="docs" />
            <StatBadge value={person.stats.flights} label="flights" />
            <StatBadge value={person.stats.connections} label="links" />
            <StatBadge value={person.stats.emails} label="emails" />
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── PersonExplorer ──────────────────────────────────────────

export default function PersonExplorer({
  onSelectPerson,
  selectedSlug,
  filters,
}: PersonExplorerProps) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('connections');
  const [blackBookOnly, setBlackBookOnly] = useState(false);
  const [page, setPage] = useState(1);
  const parentRef = useRef<HTMLDivElement>(null);
  const PER_PAGE = 100;

  const { data, isLoading } = usePersons({
    q: search || undefined,
    category: category || undefined,
    black_book: blackBookOnly || undefined,
    sort,
    page,
    per_page: PER_PAGE,
  });

  const persons = data?.data || [];
  const meta = data?.meta;

  // Single-column virtualizer: each virtual item is one card
  const virtualizer = useVirtualizer({
    count: persons.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 130,
    overscan: 8,
  });

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setPage(1);
    },
    [],
  );

  const handleCategoryClick = useCallback(
    (cat: string) => {
      setCategory((prev) => (prev === cat ? '' : cat));
      setPage(1);
    },
    [],
  );

  const totalPages = meta ? Math.ceil(meta.total / PER_PAGE) : 1;
  const showingFrom = meta ? (page - 1) * PER_PAGE + 1 : 0;
  const showingTo = meta ? Math.min(page * PER_PAGE, meta.total) : 0;

  return (
    <div className="person-explorer">
      {/* ── Toolbar ── */}
      <div className="person-toolbar">
        <div className="person-search-wrap">
          <svg className="person-search-icon" viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
          <input
            type="text"
            className="person-search"
            placeholder="Search by name, alias, or role\u2026"
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        {/* Category pills */}
        <div className="person-category-row">
          <button
            className={`person-pill${!category ? ' active' : ''}`}
            onClick={() => { setCategory(''); setPage(1); }}
          >
            All
          </button>
          {CATEGORIES.map((cat) => {
            const p = getCategoryStyle(cat);
            return (
              <button
                key={cat}
                className={`person-pill${category === cat ? ' active' : ''}`}
                onClick={() => handleCategoryClick(cat)}
                style={
                  {
                    '--pill-bg': p.bg,
                    '--pill-text': p.text,
                    '--pill-border': p.border,
                  } as React.CSSProperties
                }
              >
                {formatLabel(cat)}
              </button>
            );
          })}
        </div>

        {/* Sort & Black Book toggle */}
        <div className="person-controls-row">
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="connections">Most Connected</option>
            <option value="documents">Most Documents</option>
            <option value="flights">Most Flights</option>
            <option value="name">Alphabetical</option>
          </select>

          <label className="person-bb-toggle">
            <input
              type="checkbox"
              checked={blackBookOnly}
              onChange={(e) => { setBlackBookOnly(e.target.checked); setPage(1); }}
            />
            <span className="person-bb-toggle-label">Black Book</span>
          </label>
        </div>
      </div>

      {/* ── Pagination bar ── */}
      {meta && (
        <div className="person-pagination-bar">
          <span className="person-pagination-info">
            {meta.total > 0
              ? `${showingFrom}\u2013${showingTo} of ${meta.total.toLocaleString()}`
              : 'No results'}
          </span>
          {totalPages > 1 && (
            <div className="person-pagination-controls">
              <button
                className="person-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <span className="person-page-indicator">
                {page} / {totalPages}
              </span>
              <button
                className="person-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Card list (virtualized) ── */}
      <div className="person-grid" ref={parentRef}>
        {isLoading ? (
          <div className="person-loading">
            <div className="person-loading-spinner" />
            <span>Loading persons\u2026</span>
          </div>
        ) : persons.length === 0 ? (
          <div className="person-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
            <span>No persons match your filters</span>
          </div>
        ) : (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((vItem) => {
              const person = persons[vItem.index];
              if (!person) return null;
              return (
                <div
                  key={person.slug}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vItem.start}px)`,
                  }}
                >
                  <PersonCard
                    person={person}
                    selected={selectedSlug === person.slug}
                    onClick={() => onSelectPerson(person.slug)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
